import React, {
  ChangeEvent,
  Fragment,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  AvailableWorkflowModel,
  WorkflowAvailableProps,
} from "../../models/SkillsData";
import "./AvailableWorkflow.scss";
import {
  Box,
  Button,
  Checkbox,
  Grid,
  IconButton,
  Link,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import CloseIcon from "@mui/icons-material/Close";
import { useNavigate } from "react-router-dom";
import { useAPI } from "../../../../shared/services/api/API";
import { OPTIVAL_CONSTANTS } from "../../../../shared/Constants";
import { WorkflowConstants } from "../../../workflow/models/Workflow";
import { Order, TableHeadCell } from "../../../../shared/models/TableModels";
import { tableSorting } from "../../../../shared/utils/Utils";
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { visuallyHidden } from "@mui/utils";
import { useSkillsUtils } from "../../components/skills/SkillsUtil";
import { SkillsContext } from "../../contexts/SkillsContext";
import { MapWorkflowDialog } from "../map-workflow/MapWorkflowDialog";

export const AvailableWorkflow = (props: WorkflowAvailableProps) => {
  const {
    levelId,
    levelName,
    row,
    collapseClose,
    selectGroupsList,
    workflowDataList,
    eligibleUsersDetail,
    assignedIds,
    fetchWorkflowList,
    callFrom,
  } = props;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { updateValidateLessMore } = useContext(SkillsContext);
  const { httpPost, handleAlertBar } = useAPI();
  const [workflowDataVisible, setWorkflowDataVisible] = useState<
    AvailableWorkflowModel[]
  >([]);
  const { getAvailableWFHeaderCell } = useSkillsUtils();
  const [perPageRows, setPerPageRows] = useState<number>(5);
  const [selected, setSelected] = useState<readonly number[]>([]);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState<Order>("desc");
  const [orderBy, setOrderBy] = useState<string>("name");
  const [anchorGroupEl, setGroupAnchorEl] = useState<null | HTMLElement>(null);
  const openGroup = Boolean(anchorGroupEl);
  const [tableHeaderCell, setTableHeaderCell] = useState<TableHeadCell[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  const handleGroupClick = (
    event: React.MouseEvent<HTMLElement>,
    groupId: number
  ) => {
    setGroupAnchorEl(event.currentTarget);
  };
  const handleGroupClose = () => {
    setGroupAnchorEl(null);
  };

  const viewAllWorkflows = () => {
    navigate("../instrukt", {
      state: {
        row: {
          id: row?.skillId,
          label: row.skillName,
          levelName: levelName,
          levelId: levelId,
        },
      },
    });
  };

  const assignWorkflows = async () => {
    let reqBody: any = {
      workflowIds: selected,
    };
    if (callFrom !== "matrix") {
      reqBody["groupIds"] = [selectGroupsList[0].groupId];
    } else {
      reqBody["employeeIds"] = [row?.empId];
    }
    await httpPost(
      callFrom !== "matrix"
        ? `${OPTIVAL_CONSTANTS.API_URLS.SKILLS_ASSIGNED_WORKFLOW_GROUPS}`
        : `${OPTIVAL_CONSTANTS.API_URLS.SKILLS_ASSIGNED_WORKFLOW}`,
      reqBody
    ).then(
      (response) => {
        if (response && response.data && response.data.error) {
          handleAlertBar("error", response.data.message);
        } else {
          handleAlertBar(
            "success",
            t("opva.workflowAssignedGroupSuccessfully.")
          );
          collapseClose();
        }
      },
      (error) => { }
    );
  };

  const handleSelectCheckbox = (
    event: ChangeEvent<HTMLInputElement>,
    id: number
  ) => {
    const selectedIndex = selected.indexOf(id);
    let newSelected: readonly number[] = [];

    if (selectedIndex === -1) {
      workflowDataList
        .filter(
          (workflow: AvailableWorkflowModel) =>
            workflow?.workflowWithVersionDto?.id === id
        )
        .map((workflow: AvailableWorkflowModel) => {
          workflow.isChecked = true;
          return workflow;
        });
      newSelected = [...selected, id];
    } else {
      workflowDataList
        .filter(
          (workflow: AvailableWorkflowModel) =>
            workflow?.workflowWithVersionDto?.id === id
        )
        .map((workflow: AvailableWorkflowModel) => {
          workflow.isChecked = false;
          return workflow;
        });
      newSelected = selected?.filter((selectedId) => selectedId !== id);
    }
    setSelected(newSelected);
  };

  const removeSelectedWorkflow = () => {
    const assignIds = assignedIds;
    setSelected(assignIds);
  };

  const isSelected = (id: number) => selected.indexOf(id) !== -1;

  const renderTruncateTitle = (title: string, whereTo: string): string => {
    return title.length > WorkflowConstants.WORKFLOW_TITLE_LENGTH
      ? whereTo === "display"
        ? title.substring(0, WorkflowConstants.WORKFLOW_TITLE_LENGTH) + "..."
        : title
      : whereTo === "display"
        ? title
        : "";
  };

  const handleEmptyPage = (data: any) => {
    const sliceArr = data.slice(
      page * perPageRows,
      page * perPageRows + perPageRows
    );
    if (!sliceArr.length) {
      setPage(page === 0 ? 0 : page - 1);
    }
  };

  useMemo(() => {
    handleEmptyPage(workflowDataList);
    const checkedRowIds = workflowDataList
      .filter((workflow: AvailableWorkflowModel) => workflow?.isChecked)
      .map(
        (workflow: AvailableWorkflowModel) =>
          workflow?.workflowWithVersionDto?.id
      );
    setSelected(checkedRowIds);
    const propertyType = ["groupsAssigned", "noOfVersions"].includes(orderBy)
      ? "number"
      : "string";
    const sortedList = tableSorting(
      workflowDataList,
      order,
      orderBy,
      propertyType
    );
    setWorkflowDataVisible(
      sortedList.slice(page * perPageRows, page * perPageRows + perPageRows)
    );
  }, [order, orderBy, page, perPageRows, workflowDataList]);

  const handleSorting = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };
  useEffect(() => {
    setTableHeaderCell(getAvailableWFHeaderCell());
  }, [workflowDataVisible]);

  const checkedWorkflow = () => {
    if (selected.length === 0 && workflowDataList.length === 0) return false;
    for (let workflow of workflowDataList) {
      const isPresent = selected.some(
        (id: any) => id === workflow?.workflowWithVersionDto?.id
      );
      if (!isPresent) {
        return false;
      }
    }
    return true;
  };

  const handleAllCheckBoxForWorkflow = (event: any) => {
    if (event.target.checked) {
      const uniqueWorkFlowsIds: number[] = [];
      workflowDataList.forEach((workflow: AvailableWorkflowModel) => {
        const index = selected.findIndex(
          (id: number) => id === workflow?.workflowWithVersionDto?.id
        );
        workflow.isChecked = true;
        if (index === -1) {
          uniqueWorkFlowsIds.push(workflow?.workflowWithVersionDto?.id);
        }
      });
      setSelected([...selected, ...uniqueWorkFlowsIds]);
    } else {
      removeSelectedWorkflow();
    }
  };

  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
    const startIndex = newPage * perPageRows;
    setWorkflowDataVisible([
      ...workflowDataList.slice(startIndex, startIndex + perPageRows),
    ]);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setPerPageRows(parseInt(event.target.value, 10));
    setPage(0);
    setWorkflowDataVisible(
      workflowDataList.slice(0, parseInt(event.target.value, 10))
    );
  };

  const handleNotify = async (data: AvailableWorkflowModel) => {
    let reqBody: any = {
      workflowId: data?.workflowWithVersionDto?.id,
      skillId: row?.skillId,
      skillLevelId: levelId,
    };
    let url = `${OPTIVAL_CONSTANTS.API_URLS.NOTIFICATION_NOTIFY}`;
    if (callFrom !== "matrix") {
      reqBody["groupId"] = selectGroupsList[0].groupId;
    } else {
      reqBody["employeeId"] = row?.empId;
      url = `${OPTIVAL_CONSTANTS.API_URLS.NOTIFICATION_NOTIFY_USER}`;
    }
    await httpPost(url, reqBody).then(
      (response) => {
        if (response && response.data && response.data.error) {
          handleAlertBar("error", response.data.message);
        } else {
          handleAlertBar("success", t("opva.incompleteWorkflowNotify"));
        }
      },
      (error) => { }
    );
  };
  const closeDialog = () => {
    setIsOpen(false);
    fetchWorkflowList();
  };
  return (
    <React.Fragment>
      <div className="available-workflow-container">
        <Grid container>
          <Grid item xs={6} md={10}>
            <Typography variant="h6" className="mb-3">
              {t(`opva.${levelId}WorkflowsAvailable`)}
            </Typography>
          </Grid>
          <Grid item xs={4} md={1.5}>
            <Button
              variant="outlined"
              color="primary"
              sx={{ textTransform: "none", backgroundColor: "#FFFF" }}
              onClick={viewAllWorkflows}
              disabled={workflowDataVisible.length === 0}
            >
              {t("opva.viewAllWorkflows")}
            </Button>
          </Grid>
          <Grid item xs={2} md={0.5}>
            <Link
              component="button"
              className="float-end"
              title="Close"
              onClick={collapseClose}
            >
              <CloseIcon />
            </Link>
          </Grid>
        </Grid>
        <div className="d-flex align-items-center justify-content-start pb-8">
          <Typography variant="bodySmall">{`${selected.length}/${workflowDataList.length} selected`}</Typography>
        </div>
        {assignedIds.length < selected.length && (
          <Typography
            variant="bodySmall"
            color={"#0084F4"}
            style={{
              cursor: "pointer",
            }}
            onClick={() => removeSelectedWorkflow()}
          >
            {t("opva.deselectAll")}
          </Typography>
        )}
        {workflowDataVisible && workflowDataVisible.length ? (
          <TableContainer className="workflow-table-container">
            <Table
              stickyHeader
              sx={{ width: "100%", overflow: "auto", maxHeight: "20%" }}
              size="medium"
              aria-label="Available Workflow table"
            >
              <TableHead>
                <TableRow key="available-workflow-table-header">
                  <TableCell
                    size="small"
                    padding="checkbox"
                    sx={{ textAlign: "center", verticalAlign: "middle" }}
                  >
                    <Checkbox
                      sx={{
                        padding: 0,
                        display: "block",
                        margin: "0 auto",
                      }}
                      checked={checkedWorkflow()}
                      onChange={(e) => handleAllCheckBoxForWorkflow(e)}
                    />
                  </TableCell>
                  {callFrom !== "matrix" &&
                    tableHeaderCell.map((headCell: TableHeadCell) => (
                      <TableCell
                        key={headCell.id}
                        sortDirection={
                          orderBy === headCell.sortingProperty ? order : false
                        }
                        align={headCell.position}
                        sx={{
                          width: headCell.width + "%",
                        }}
                      >
                        {headCell.sorting ? (
                          <TableSortLabel
                            active={orderBy === headCell.sortingProperty}
                            direction={
                              orderBy === headCell.sortingProperty
                                ? order
                                : "asc"
                            }
                            onClick={() =>
                              handleSorting(headCell.sortingProperty)
                            }
                          >
                            {headCell.label}
                            {orderBy === headCell.id ? (
                              <Box component="span" sx={visuallyHidden}>
                                {orderBy === "desc"
                                  ? "sorted descending"
                                  : "sorted ascending"}
                              </Box>
                            ) : null}
                          </TableSortLabel>
                        ) : (
                          headCell.label
                        )}
                      </TableCell>
                    ))}
                  {callFrom == "matrix" &&
                    tableHeaderCell
                      .filter(
                        (headCell: TableHeadCell) => headCell.id !== "inprogress"
                      )
                      .map((headCell: TableHeadCell) => (
                        <TableCell
                          key={headCell.id}
                          sortDirection={
                            orderBy === headCell.sortingProperty ? order : false
                          }
                          align={headCell.position}
                          sx={{
                            width: headCell.width + "%",
                          }}
                        >
                          {headCell.sorting ? (
                            <TableSortLabel
                              active={orderBy === headCell.sortingProperty}
                              direction={
                                orderBy === headCell.sortingProperty
                                  ? order
                                  : "asc"
                              }
                              onClick={() =>
                                handleSorting(headCell.sortingProperty)
                              }
                            >
                              {headCell.label}
                              {orderBy === headCell.id ? (
                                <Box component="span" sx={visuallyHidden}>
                                  {orderBy === "desc"
                                    ? "sorted descending"
                                    : "sorted ascending"}
                                </Box>
                              ) : null}
                            </TableSortLabel>
                          ) : (
                            headCell.label
                          )}
                        </TableCell>
                      ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {workflowDataVisible && workflowDataVisible.length ? (
                  workflowDataVisible.map(
                    (data: AvailableWorkflowModel, index) => {
                      const isItemSelected = isSelected(
                        data?.workflowWithVersionDto?.id
                      );
                      const labelId = `table-checkbox-${index}`;

                      return (
                        <Fragment key={index}>
                          <TableRow
                            role="checkbox"
                            tabIndex={-1}
                            aria-checked={isItemSelected}
                            selected={isItemSelected}
                          >
                            <TableCell
                              padding="checkbox"
                              sx={{
                                textAlign: "center",
                                verticalAlign: "middle",
                              }}
                            >
                              <Checkbox
                                color="primary"
                                checked={isItemSelected}
                                onChange={(event) =>
                                  handleSelectCheckbox(
                                    event,
                                    data?.workflowWithVersionDto?.id
                                  )
                                }
                                inputProps={{
                                  "aria-labelledby": labelId,
                                }}
                                disabled={data?.assigned}
                                sx={{
                                  padding: 0,
                                  display: "block",
                                  margin: "0 auto",
                                }}
                              />
                            </TableCell>
                            <TableCell>{row?.skillName}</TableCell>
                            <TableCell>{levelName}</TableCell>

                            <TableCell>
                              <span
                                title={renderTruncateTitle(
                                  data?.workflowWithVersionDto?.name,
                                  "title"
                                )}
                              >
                                {renderTruncateTitle(
                                  data?.workflowWithVersionDto?.name,
                                  "display"
                                )}
                              </span>
                            </TableCell>
                            <TableCell align="center">
                              {data?.workflowWithVersionDto?.publishedVersion}
                            </TableCell>
                            {callFrom !== "matrix" && (
                              <TableCell align="center">
                                {data?.completedCount}
                              </TableCell>
                            )}
                            <TableCell align="center">
                              <Link
                                component="button"
                                title={t("opva.notify")}
                                style={{
                                  pointerEvents:
                                    (callFrom !== "matrix" &&
                                      !data?.assigned || data?.completedCount <= 0) ||
                                      (callFrom === "matrix" && !data?.assigned)
                                      ? "none"
                                      : "auto",
                                  opacity:
                                    (callFrom !== "matrix" &&
                                      !data?.assigned || data?.completedCount <= 0) ||
                                      (callFrom === "matrix" && !data?.assigned)
                                      ? "0.5"
                                      : "1",
                                  cursor:
                                    (callFrom !== "matrix" &&
                                      !data?.assigned || data?.completedCount <= 0) ||
                                      (callFrom === "matrix" && !data?.assigned)
                                      ? "default"
                                      : "pointer",
                                }}
                                onClick={() => handleNotify(data)}
                                disabled={
                                  callFrom === "matrix"
                                    ? !data?.assigned
                                    : (!data?.assigned || data?.completedCount <= 0)
                                }
                              >
                                <NotificationsNoneIcon />
                              </Link>{" "}
                            </TableCell>
                          </TableRow>
                        </Fragment>
                      );
                    }
                  )
                ) : (
                  <TableRow>
                    <TableCell colSpan={8}>
                      {t("opva.noWorkflowDataAvailable")}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
            <div className="d-flex align-items-center justify-content-end">
              <TablePagination
                className="pagination-container"
                component="div"
                rowsPerPageOptions={[5, 10, 25]}
                count={workflowDataList?.length}
                page={page}
                onPageChange={handleChangePage}
                rowsPerPage={perPageRows}
                onRowsPerPageChange={handleChangeRowsPerPage}
              />
            </div>
          </TableContainer>
        ) : (
          <div className="d-flex align-items-center justify-content-start">
            <Typography variant="bodySmall" className="my-4">
              {t("opva.noWorkflowMappedToskillMsg")}{" "}
              <Link component="button" onClick={() => setIsOpen(true)}>
                {t("opva.clickHere")}
              </Link>
            </Typography>
          </div>
        )}
        {callFrom !== "matrix" && (
          <div>
            <Typography variant="h6" className="my-4">
              {t("opva.Groups/UsersSelected")}
            </Typography>
          </div>
        )}
        <div></div>
        <Grid container spacing={2} paddingBottom={3}>
          {selectGroupsList.length > 0 &&
            selectGroupsList.map((data: any, index: number) => (
              <Grid item xs={3} md={3} key={index}>
                <section className="assigned-user">
                  <div className="d-flex mb-3">
                    <img src="images/User-Thumb-Black.svg" alt="User" />
                    <div className="ms-2">
                      <Typography
                        variant="bodySmall"
                        className="d-block fw-bold"
                      >
                        {data.name}
                      </Typography>
                      <Typography variant="labelSmall" className="d-block">
                        {`${eligibleUsersDetail[data.groupId]?.eligibleUsers}/${eligibleUsersDetail[data.groupId]?.totalUsers
                          } ${t("opva.usersEligibleSelected")}`}
                      </Typography>
                    </div>
                  </div>
                  <div style={{ alignItems: "end" }}>
                    <IconButton
                      aria-label="more"
                      id="long-button"
                      aria-controls={openGroup ? "long-menu" : undefined}
                      aria-expanded={openGroup ? "true" : undefined}
                      aria-haspopup="true"
                      onClick={(e) => handleGroupClick(e, data?.groupId)}
                      color="primary"
                    >
                      <MoreHorizIcon />
                    </IconButton>
                    <Menu
                      id="demo-positioned-menu"
                      aria-labelledby="demo-positioned-button"
                      anchorEl={anchorGroupEl}
                      open={openGroup}
                      onClose={handleGroupClose}
                      anchorOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                    >
                      <MenuItem onClick={handleGroupClose}>
                        <Link component="button" title={t("opva.notify")}>
                          <NotificationsNoneIcon />
                        </Link>{" "}
                        <Typography
                          variant="bodySmall"
                          className="d-block ms-2"
                        >
                          {t("opva.notify")}
                        </Typography>
                      </MenuItem>
                    </Menu>
                  </div>
                </section>
              </Grid>
            ))}
        </Grid>
        <Button
          variant="contained"
          color="primary"
          sx={{ textTransform: "none" }}
          onClick={assignWorkflows}
          disabled={
            selected.length === 0 || assignedIds.length === selected.length
          }
        >
          {t("opva.assignWorkflows&Upskill")}
        </Button>
      </div>
      {isOpen && (
        <MapWorkflowDialog
          isOpen={isOpen}
          closeDialog={closeDialog}
          skillLevelId={levelId}
          skillId={row?.skillId}
          skillName={row?.skillName}
        />
      )}
    </React.Fragment>
  );
};
